import React from 'react';
import { AutomationTemplate } from '../../services/automationTemplateService';
import { useAdminTheme } from '../../contexts/AdminThemeContext';

interface AutomationTemplateAnalyticsProps {
  templates: AutomationTemplate[];
}

const AutomationTemplateAnalytics: React.FC<AutomationTemplateAnalyticsProps> = ({ templates }) => {
  const { isDark } = useAdminTheme();
  
  // Calculate overall statistics
  const stats = {
    totalTemplates: templates.length,
    activeTemplates: templates.filter(t => t.isActive).length,
    premiumTemplates: templates.filter(t => t.isPremium).length,
    totalUsage: templates.reduce((sum, t) => sum + (t.usageCount || 0), 0),
    totalDownloads: templates.reduce((sum, t) => sum + (t.downloadCount || 0), 0),
    totalRevenue: templates.reduce((sum, t) => sum + ((t.averageRevenue || 0) * (t.usageCount || 0)), 0),
    avgSuccessRate: templates.length > 0 
      ? templates.reduce((sum, t) => sum + (t.successRate || 0), 0) / templates.length 
      : 0
  };

  // Category breakdown
  const categoryStats = templates.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = {
        count: 0,
        usage: 0,
        downloads: 0
      };
    }
    acc[template.category].count++;
    acc[template.category].usage += template.usageCount || 0;
    acc[template.category].downloads += template.downloadCount || 0;
    return acc;
  }, {} as Record<string, { count: number; usage: number; downloads: number }>);

  // Top performing templates
  const topTemplates = [...templates]
    .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
    .slice(0, 10);

  // Recent templates
  const recentTemplates = [...templates]
    .sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
      return dateB.getTime() - dateA.getTime();
    })
    .slice(0, 5);

  // Channel breakdown
  const channelStats = templates.reduce((acc, template) => {
    if (!acc[template.channel]) {
      acc[template.channel] = 0;
    }
    acc[template.channel]++;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <div className="flex items-center justify-between mb-2">
            <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Total Usage
            </div>
            <span className="text-2xl">📊</span>
          </div>
          <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {stats.totalUsage.toLocaleString()}
          </div>
          <div className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
            Across all templates
          </div>
        </div>

        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <div className="flex items-center justify-between mb-2">
            <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Total Downloads
            </div>
            <span className="text-2xl">⬇️</span>
          </div>
          <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {stats.totalDownloads.toLocaleString()}
          </div>
          <div className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
            Template downloads
          </div>
        </div>

        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <div className="flex items-center justify-between mb-2">
            <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Avg Success Rate
            </div>
            <span className="text-2xl">✅</span>
          </div>
          <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {stats.avgSuccessRate.toFixed(1)}%
          </div>
          <div className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
            Average across templates
          </div>
        </div>

        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <div className="flex items-center justify-between mb-2">
            <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Estimated Revenue
            </div>
            <span className="text-2xl">💰</span>
          </div>
          <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            ${stats.totalRevenue.toLocaleString()}
          </div>
          <div className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
            From premium templates
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing Templates */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow`}>
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              🏆 Top Performing Templates
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              By usage count
            </p>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {topTemplates.length === 0 ? (
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No templates yet</p>
              ) : (
                topTemplates.map((template, index) => (
                  <div 
                    key={template.id}
                    className={`flex items-center justify-between p-3 rounded ${
                      isDark ? 'bg-gray-700' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        index === 0 ? 'bg-yellow-500 text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-orange-600 text-white' :
                        isDark ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {template.name}
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {template.category}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {template.usageCount || 0}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        uses
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow`}>
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              📂 Category Breakdown
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Templates by category
            </p>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {Object.keys(categoryStats).length === 0 ? (
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No categories yet</p>
              ) : (
                Object.entries(categoryStats)
                  .sort((a, b) => b[1].count - a[1].count)
                  .map(([category, data]) => (
                    <div 
                      key={category}
                      className={`flex items-center justify-between p-3 rounded ${
                        isDark ? 'bg-gray-700' : 'bg-gray-50'
                      }`}
                    >
                      <div>
                        <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {data.usage} total usage
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {data.count}
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          templates
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>

        {/* Channel Distribution */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow`}>
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              📱 Channel Distribution
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Templates by channel type
            </p>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {Object.entries(channelStats).map(([channel, count]) => (
                <div 
                  key={channel}
                  className={`p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {channel}
                    </span>
                    <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {count}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full"
                      style={{ width: `${(count / templates.length) * 100}%` }}
                    ></div>
                  </div>
                  <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {((count / templates.length) * 100).toFixed(1)}% of all templates
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Templates */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow`}>
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              🆕 Recently Added
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Latest template additions
            </p>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {recentTemplates.length === 0 ? (
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No templates yet</p>
              ) : (
                recentTemplates.map((template) => {
                  const createdDate = template.createdAt?.toDate?.() || new Date(template.createdAt);
                  const daysAgo = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
                  
                  return (
                    <div 
                      key={template.id}
                      className={`p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {template.name}
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {template.category} • {template.channel}
                          </div>
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo}d ago`}
                        </div>
                      </div>
                      {template.isRecommended && (
                        <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                          ⭐ Recommended
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Template Status Overview */}
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow`}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            📈 Template Status Overview
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className={`text-3xl font-bold text-green-600`}>{stats.activeTemplates}</div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Active</div>
            </div>
            <div className="text-center">
              <div className={`text-3xl font-bold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {stats.totalTemplates - stats.activeTemplates}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Inactive</div>
            </div>
            <div className="text-center">
              <div className={`text-3xl font-bold text-purple-600`}>{stats.premiumTemplates}</div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Premium</div>
            </div>
            <div className="text-center">
              <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {stats.totalTemplates - stats.premiumTemplates}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Free</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationTemplateAnalytics;

